<template>
  <div v-if="user" class="subscription-page flex-1 pb-6 px-6 pt-3">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-2xl font-bold text-foreground">Subscription</h1>
        <p class="text-muted-foreground text-sm mt-1">
          Manage your subscription plans and payment methods
        </p>
      </div>
    </div>
    <hr class="mb-6">

    <div v-if="!user.subscribed && (user.agency && !user.agency.free_fee) && (user.trial_past || user.user_plans.length && user.user_plans.filter(o => o.past_due === 1).length)" class="alert alert-danger" role="alert">
      Please subscribe to a package, or update your payment method.
    </div>
    <b-modal v-model="showPaymentModal" title="Add Payment Method">
      <AddCreditCard ref="cardForm" />
      <template v-slot:modal-footer>
        <button
            class="btn btn-primary w-100"
            :disabled="loadingSubscription"
            @click="validateCard"
        >
          <b-spinner v-if="loadingSubscription" small />
          <span v-else>ADD PAYMENT METHOD</span>
        </button>
      </template>
    </b-modal>
    <div>
      <div v-if="!loadingPlans" class="subscription-plans-grid">
        <div v-for="plan in plans" :key="plan.id" class="subscription-plan-card">
          <div class="card h-100">
            <div class="card-body d-flex flex-column">
              <div class="plan-header-top mb-3">
                <div class="plan-icon-small">
                  <feather type="package" />
                </div>
                <h5 class="plan-name">{{ plan.name }}</h5>
              </div>
              
              <div class="plan-pricing mb-3">
                <div class="plan-price-main">
                  <span class="price-amount">${{ plan.amount }}</span>
                  <span class="price-period">
                    <span v-if="plan.type === 'one_time'">One Time</span>
                    <span v-else-if="plan.recurring.interval === 'year' && plan.recurring.interval_count === 1">/year</span>
                    <span v-else-if="plan.recurring.interval === 'month' && plan.recurring.interval_count === 1">/mo</span>
                    <span v-else-if="plan.recurring.interval === 'day'">/day</span>
                  </span>
                </div>
                <div v-if="plan.recurring && plan.recurring.interval === 'month'" class="plan-price-secondary">
                  ${{ (plan.amount * 3).toFixed(0) }} for 3 months
                </div>
              </div>
              
              <div class="plan-divider mb-3"></div>
              
              <div class="plan-features mb-4 flex-grow-1">
                <div class="plan-feature-item">
                  <div class="feature-icon">
                    <feather type="check-circle" />
                  </div>
                  <div class="feature-content">
                    <div class="feature-main">
                      <span
                        v-b-tooltip.hover
                        :title="plan.desc"
                        class="feature-text"
                      >
                        {{ plan.desc || 'Subscription plan' }}
                      </span>
                    </div>
                    <div v-if="plan.recurring && plan.recurring.interval === 'month'" class="feature-secondary">
                      Included in your plan
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="plan-action">
                <button
                  class="btn btn-subscribe w-100"
                  :class="{
                    'btn-primary': !(user.user_plans.length > 0 && user.user_plans.filter(o => plan.id === o.plan.stripe_price_id).length),
                    'btn-active': (user.user_plans.length > 0 && user.user_plans.filter(o => plan.id === o.plan.stripe_price_id).length),
                  }"
                  :disabled="
                    loadingSubscription !== null || (user.user_plans.length > 0 && user.user_plans.filter(o => plan.id === o.plan.stripe_price_id).length > 0)
                  "
                  @click="subscription(plan.id)"
                >
                  <b-spinner v-if="loadingSubscription === plan.id" small class="mr-2" />
                  <span v-else class="d-flex align-items-center justify-content-center">
                    <span v-if="user.user_plans.length > 0 && user.user_plans.filter(o => plan.id === o.plan.stripe_price_id).length" class="d-flex align-items-center">
                      <feather type="check-circle" class="btn-icon mr-2" />
                      Active
                    </span>
                    <span v-else class="d-flex align-items-center">
                      <feather type="arrow-right" class="btn-icon mr-2" />
                      Purchase Now
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center mt-5">
        <b-spinner large></b-spinner>
      </div>
    </div>
  </div>
</template>

<script>
import AddCreditCard from '../Common/AddCreditCard'

export default {
  components: { AddCreditCard },
  props:{
    typeFilter:{
      default:null,
      type:String
    }
  },
  data() {
    return {
      showPaymentModal:false,
      loadingPlans: false,
      loadingSubscription: null,
      showAddCard: false,
      loadingCreateCard: false,
      selectedPlan: null,
      loadingPaymentMethod: false,
      loadingSetDefault: false,
      loadingStopImpersonate: false,
      errorMsg:null
    }
  },

  computed: {
    isImpersonating() {
      return this.$store.getters['auth/isImpersonating']
    },

    plans() {
      let plans = this.$store.getters['subscription/allPlans'] || [];
      // Get all plan IDs from user_plans
      const userPlanIds = (this.user && this.user.user_plans)
        ? this.user.user_plans.map(up => up.plan && up.plan.id).filter(Boolean)
        : [];
      return plans.filter(
        (plan) => (
          this.typeFilter !== null
            ? plan.hasOwnProperty('db_plan') && plan.db_plan !== null && plan.db_plan.product_type === this.typeFilter
            : true
        ) && (
          plan.visible || plan.db_plan !== null && userPlanIds.includes(plan.db_plan.id)
        )
      )
    },

    user() {
      return this.$store.getters['auth/user']
    },

    paymentMethods() {
      return this.$store.getters['paymentMethod/all']
    }

  },
  updated(){
    this.errorMessage();
  },
  mounted() {
    this.getPlans()
  },

  methods: {
    errorMessage(){
      this.errorMsg = sessionStorage.getItem('sub-error-msg');
    },
    logout() {
      this.$store
        .dispatch('auth/logout')
        .then(() => {
          this.$router.push({ name: 'login' })
        })
        .catch(() => {})
    },

    onSuccessHandler() {
      if (this.user.agency) {
        if(this.typeFilter !== null){
          setTimeout(function(){
            // TODO: Setup events instead of relying on a timeout
            window.location.reload();
          },3000);
        }else {
          if(window.location.origin.includes('repuagency.com')){
            this.$router.push({name: 'agency.customers.index'})
          }else{
            this.$router.push({name: 'agency.dashboard'})
          }
        }
      } else {
        this.getPlans()
      }
    },

    onFailedHandler() {},

    getPlans() {
      if (this.$store.getters['subscription/allPlans']) return

      this.loadingPlans = true
      this.$store
        .dispatch('subscription/getAllPlans')
        .then(() => {
          this.loadingPlans = false
        })
        .catch(() => {
          this.loadingPlans = false
        })
    },

    subscription(plan) {
      this.selectedPlan = plan;
      if(this.user.default_payment_method){
        this.loadingSubscription = plan

        this.$store
            .dispatch('subscription/subscribe', plan)
            .then(() => {
              let that = this;
              setTimeout(function(){
                // TODO: Setup events instead of relying on a timeout
                that.loadingSubscription = null
                window.location.reload();
              },3000);
            })
            .catch(() => {
              this.loadingSubscription = null
            })
      }else{
        this.showPaymentModal = true;
      }

    },

    createPaymentMethod(paymentMethodId) {
      this.$store
        .dispatch('paymentMethod/create', paymentMethodId)
        .then(() => {
          this.$store
            .dispatch('subscription/subscribe', this.selectedPlan)
            .then(() => {
              this.loadingSubscription = null
              this.onSuccessHandler()
            })
            .catch(() => {
              this.loadingSubscription = null
              this.getPlans()
            })
        })
        .catch(() => {
          this.loadingSubscription = null
        })
    },

    setDefaultPaymentMethod(paymentMethodId) {
      this.loadingSetDefault = true
      this.$store
        .dispatch('paymentMethod/setDefault', paymentMethodId)
        .then((res) => {
          this.$store
            .dispatch('subscription/resume')
            .then(() => {
              this.loadingSetDefault = false
              this.onSuccessHandler()
            })
            .catch(() => {
              this.loadingSetDefault = false
              this.getPlans()
            })
        })
        .catch((errors) => {
          this.loadingSetDefault = false
        })
    },

    getPaymentMethods() {
      this.loadingPaymentMethod = true
      this.$store
        .dispatch('paymentMethod/getAll')
        .then((res) => {
          this.loadingPaymentMethod = false
        })
        .catch(() => {
          this.loadingPaymentMethod = false
        })
    },

    async validateCard() {
      this.loadingSubscription = true

      await this.$refs.cardForm
        .validate()
        .then((paymentMethodId) => {
          this.createPaymentMethod(paymentMethodId);
        })
        .catch(() => {
          this.loadingSubscription = null;
        })
    },

    stopImpersonate() {
      this.loadingStopImpersonate = true

      this.$store
        .dispatch('auth/stopImpersonate')
        .then(() => {
          this.$store
            .dispatch('auth/getProfile')
            .then((user) => {
              this.$router.push('/admin')
              this.loadingStopImpersonate = false
            })
            .catch(() => {
              this.loadingStopImpersonate = false
            })
        })
        .catch(() => {
          this.loadingStopImpersonate = false
        })
    },

  },
}
</script>

<style lang="scss">
.subscription-page {
  .subscription-plans-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
  }

  .subscription-plan-card {
    .card {
      border: 1px solid #e9ecef;
      border-radius: 0.75rem;
      transition: all 0.2s ease;
      height: 100%;
      background: #ffffff;

      &:hover {
        border-color: #ced4da;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      }
    }

    .card-body {
      padding: 1.5rem;
      display: flex;
      flex-direction: column;
    }

    .plan-header-top {
      display: flex;
      align-items: center;
      gap: 0.75rem;

      .plan-icon-small {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        flex-shrink: 0;

        svg {
          width: 24px;
          height: 24px;
          color: #28a745;
          stroke-width: 2;
        }
      }

      .plan-name {
        font-size: 1.125rem;
        font-weight: 600;
        color: #212529;
        line-height: 1.4;
        margin: 0;
      }
    }

    .plan-pricing {
      .plan-price-main {
        display: flex;
        align-items: baseline;
        gap: 0.25rem;
        margin-bottom: 0.25rem;

        .price-amount {
          font-size: 2.25rem;
          font-weight: 700;
          color: #212529;
          line-height: 1;
        }

        .price-period {
          font-size: 1rem;
          font-weight: 400;
          color: #6c757d;
        }
      }

      .plan-price-secondary {
        font-size: 0.8125rem;
        color: #6c757d;
        line-height: 1.4;
      }
    }

    .plan-divider {
      height: 1px;
      background-color: #e9ecef;
      margin: 0;
    }

    .plan-features {
      .plan-feature-item {
        display: flex;
        gap: 0.75rem;
        align-items: flex-start;

        .feature-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          flex-shrink: 0;
          margin-top: 2px;

          svg {
            width: 18px;
            height: 18px;
            color: #28a745;
            stroke-width: 2.5;
          }
        }

        .feature-content {
          flex: 1;
          min-width: 0;

          .feature-main {
            margin-bottom: 0.25rem;

            .feature-text {
              font-size: 0.9375rem;
              font-weight: 600;
              color: #212529;
              line-height: 1.5;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
              text-overflow: ellipsis;
              word-break: break-word;
            }
          }

          .feature-secondary {
            font-size: 0.8125rem;
            color: #6c757d;
            line-height: 1.4;
          }
        }
      }
    }

    .plan-action {
      margin-top: auto;
      padding-top: 0.5rem;
    }

    .btn-subscribe {
      font-size: 0.9375rem;
      font-weight: 600;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      transition: all 0.2s ease;
      border: none;
      
      &.btn-primary {
        background-color: #28a745 !important;
        color: #ffffff;

        &:hover:not(:disabled) {
          background-color: #218838 !important;
        }

        svg {
          color: #ffffff;
        }
      }
      
      &.btn-active {
        background-color: #28a745 !important;
        color: #ffffff;
        opacity: 0.8;
        cursor: not-allowed;

        &:hover {
          background-color: #28a745 !important;
          opacity: 0.8;
        }

        svg {
          color: #ffffff;
        }
      }
      
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .btn-icon {
        width: 16px;
        height: 16px;
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;
        
        svg {
          width: 16px;
          height: 16px;
          display: block;
        }
      }
    }
  }

  .payment-methods {
    .payment-method {
      padding: 10px 0;
      .card-brand {
        font-size: 16px;
        font-weight: 500;
        color: #2c2e2f;
        text-transform: uppercase;
      }
      .card-bumber,
      .card-expiration {
        font-weight: 500;
        color: #666;
      }
    }
  }
  .payment-methods .payment-method .actions {
    margin-top: 10px;
  }
  .payment-methods .payment-method .actions a[disabled] {
    opacity: 0.6;
  }
  .logout-button {
    max-width: 200px;
    position: absolute;
    top: 50px;
    right: 50px;
  }
}
</style>
